import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { ApiError, api } from '../lib/api';
import { Button, Card, CardBody, EmptyState, Field, Input, LoadingBlock, Select, useOptionalToast } from '../components/ui';

interface RegistrationOptions {
  club: string;
  team: string;
  mailEnabled: boolean;
  persons: { id: string; name: string }[];
}

/**
 * Selbstregistrierung über den zentralen Mannschaftslink.
 *
 * Das Merkmal steht im URL-Fragment und wird daher nie mit der Seitenadresse
 * an den Server geschickt. Anders als beim persönlichen Link bleibt es in der
 * Adresse stehen: wer sich vertippt, soll neu laden können.
 */
export function RegistrationPage(): JSX.Element {
  const toast = useOptionalToast();
  const token = window.location.hash.slice(1);
  const [personId, setPersonId] = useState('');
  const [email, setEmail] = useState('');
  const [email2, setEmail2] = useState('');
  const [bestaetigen, setBestaetigen] = useState(false);
  const [fertig, setFertig] = useState<string | null>(null);

  const options = useQuery({
    queryKey: ['sports-registration', token],
    queryFn: () => api.post<RegistrationOptions>('/api/public/sports-registration/options', { token }),
    enabled: token.length >= 32,
    retry: false,
  });

  const claim = useMutation({
    mutationFn: () => api.post('/api/public/sports-registration/claim', { token, personId, email: email.trim() }),
    onSuccess: () => setFertig(email.trim()),
    // Die Meldung steht zusätzlich im Formular; der Hinweis fällt auch bei langen Seiten auf.
    onError: (error: unknown) =>
      toast?.show(error instanceof ApiError ? error.message : 'Anmeldung fehlgeschlagen', 'error'),
  });

  const data = options.data;
  const fehler = claim.error instanceof ApiError ? claim.error.message : claim.error ? 'Das hat nicht geklappt. Bitte später erneut versuchen.' : null;
  const emailGueltig = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const gleich = email.trim().toLowerCase() === email2.trim().toLowerCase();
  const abweichend = email2.trim() !== '' && !gleich;

  return (
    <div className="mx-auto min-h-screen w-full max-w-lg px-4 py-6 sm:py-10">
      <header className="mb-4">
        <p className="text-xs uppercase tracking-wide text-slate-400">{data?.club || 'UK Info'}</p>
        <h1 className="page-title">{data?.team ? `${data.team}: Anmeldung` : 'Anmeldung zur Mannschaft'}</h1>
        <p className="page-subtitle">Einmalig Namen wählen – danach kommt dein persönlicher Link per E-Mail.</p>
      </header>

      {options.isLoading && <LoadingBlock label="Link wird geprüft …" />}
      {(token.length < 32 || options.error) && (
        <EmptyState title="Dieser Anmeldelink gilt nicht mehr" description="Bitte frage die Mannschaftsleitung nach einem aktuellen Link." />
      )}

      {data && fertig && (
        <Card><CardBody className="space-y-2">
          <p className="font-semibold">Fast geschafft!</p>
          <p className="text-sm text-slate-600">
            Wir haben deinen persönlichen Link an <strong>{fertig}</strong> geschickt. Öffne ihn auf dem Gerät, mit dem du
            zu- und absagen möchtest. Die Mail kommt in wenigen Minuten – sieh auch im Spam-Ordner nach. Keine E-Mail bekommen? Dann melde dich bei der Mannschaftsleitung.
          </p>
        </CardBody></Card>
      )}

      {data && !fertig && !data.mailEnabled && (
        <EmptyState title="Anmeldung derzeit nicht möglich" description="Der E-Mail-Versand ist nicht eingerichtet. Bitte die Mannschaftsleitung um deinen persönlichen Link bitten." />
      )}

      {data && !fertig && data.mailEnabled && data.persons.length === 0 && (
        <EmptyState
          title="Hier ist kein Name mehr frei"
          description="Hat dir die Mannschaftsleitung schon einen persönlichen Link geschickt? Dann öffne ihn aus WhatsApp oder deiner Mail. Fehlt dein Name ganz, melde dich bei der Mannschaftsleitung."
        />
      )}

      {data && !fertig && data.mailEnabled && data.persons.length > 0 && (
        <Card><CardBody>
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              if (!personId || !emailGueltig || !gleich) return;
              if (!bestaetigen) { setBestaetigen(true); return; }
              claim.mutate();
            }}
          >
            <Field label="Wer bist du?" required hint="Jeder Name lässt sich nur einmal wählen">
              {(id) => (
                <Select id={id} value={personId} onChange={(event) => { setPersonId(event.target.value); setBestaetigen(false); }}>
                  <option value="">Namen wählen …</option>
                  {data.persons.map((person) => <option key={person.id} value={person.id}>{person.name}</option>)}
                </Select>
              )}
            </Field>
            <Field label="E-Mail-Adresse" required hint="Dorthin schicken wir deinen persönlichen Link">
              {(id) => <Input id={id} type="email" autoComplete="email" value={email} onChange={(event) => { setEmail(event.target.value); setBestaetigen(false); }} placeholder="name@beispiel.at" />}
            </Field>
            <Field label="E-Mail-Adresse wiederholen" required error={abweichend ? 'Die Adressen stimmen nicht überein' : undefined}>
              {(id) => <Input id={id} type="email" autoComplete="off" value={email2} onChange={(event) => { setEmail2(event.target.value); setBestaetigen(false); }} placeholder="name@beispiel.at" />}
            </Field>
            {bestaetigen && (
              <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
                Stimmt die Adresse? <strong>{email.trim()}</strong> – dorthin geht dein persönlicher Link, ein Tippfehler ist später schwer zu beheben.
              </p>
            )}
            {fehler && <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-800">{fehler}</p>}
            <div className="flex flex-wrap gap-2">
              <Button type="submit" disabled={!personId || !emailGueltig || !gleich} loading={claim.isPending}>
                {bestaetigen ? 'Ja, Link zuschicken' : 'Link zuschicken'}
              </Button>
              {bestaetigen && !claim.isPending && <Button type="button" variant="ghost" onClick={() => setBestaetigen(false)}>Adresse ändern</Button>}
            </div>
            <p className="text-xs text-slate-500">
              Die Adresse sieht nur die Mannschaftsleitung. Hast du versehentlich einen falschen Namen gewählt, kann sie die Wahl zurücksetzen.
            </p>
          </form>
        </CardBody></Card>
      )}
    </div>
  );
}
